import React, { useState, useEffect, useCallback } from 'react';
import { vehicleService } from '../../services/vehicleService';
import { FiDownload, FiCopy, FiRefreshCw, FiCheck, FiExternalLink, FiAlertCircle, FiEye, FiCode } from 'react-icons/fi';

const SITE_URL = 'https://paradise-performance.com';

const STORE_CODE = '0226';

const FEED_COLUMNS = [
  'id',
  'title',
  'description',
  'link',
  'link_template',
  'image_link',
  'price',
  'google_product_category',
  'condition',
  'brand',
  'model',
  'trim',
  'year',
  'mileage',
  'VIN',
  'color',
  'availability',
  'store_code',
  'body_style',
  'engine',
  'vehicle_option',
  'product_type'
];

function slugify(str) {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function buildFeedId(vehicle) {
  return vehicle.vin || `vehicle-${vehicle.id}`;
}

function buildTitle(v) {
  return [v.year, v.make, v.model].filter(Boolean).join(' ');
}

function buildDescription(v) {
  const parts = [];
  if (v.description) {
    // Strip HTML tags and limit length
    const clean = v.description.replace(/<[^>]*>/g, '').trim();
    if (clean.length > 0) {
      parts.push(clean.length > 500 ? clean.substring(0, 497) + '...' : clean);
    }
  }
  if (parts.length === 0) {
    // Generate a basic description
    const desc = [
      v.year, v.make, v.model,
      v.mileage ? `with ${Number(v.mileage).toLocaleString()} miles` : '',
      v.exteriorColor ? `in ${v.exteriorColor}` : '',
    ].filter(Boolean).join(' ');
    parts.push(desc + '. Clean title, well maintained.');
  }
  return parts.join(' ');
}

function buildImageLink(v) {
  const images = Array.isArray(v.images) ? v.images : [];
  if (images.length === 0) return '';
  const first = typeof images[0] === 'string' ? images[0] : images[0]?.url || images[0]?.src || '';
  if (!first) return '';
  // If already absolute URL, return as-is
  if (first.startsWith('http')) return first;
  // Otherwise prepend site URL
  return `${SITE_URL}${first.startsWith('/') ? '' : '/'}${first}`;
}

function mapCondition(v) {
  const c = (v.condition || '').toLowerCase();
  if (c.includes('new')) return 'New';
  return 'Used';
}

function mapAvailability(v) {
  const status = (v.status || '').toLowerCase();
  if (status.includes('sold')) return 'out_of_stock';
  if (status.includes('pending')) return 'preorder';
  return 'in_stock';
}

function mapBodyStyle(v) {
  const m = ((v.model || '') + ' ' + (v.vehicleType || v.vehicle_type || '')).toLowerCase();
  if (m.includes('tiguan') || m.includes('q5') || m.includes('x3') || m.includes('x5') || m.includes('rav') || m.includes('suv') || m.includes('crossover')) return 'suv';
  if (m.includes('truck') || m.includes('f-150') || m.includes('silverado') || m.includes('ram')) return 'truck';
  if (m.includes('coupe') || m.includes('tt')) return 'coupe';
  if (m.includes('convertible') || m.includes('cabrio')) return 'convertible';
  if (m.includes('wagon') || m.includes('avant') || m.includes('allroad')) return 'station_wagon';
  if (m.includes('hatch') || m.includes('gti') || m.includes('golf')) return 'hatchback';
  if (m.includes('van') || m.includes('minivan')) return 'minivan';
  return 'sedan';
}

function mapEngine(v) {
  const ft = (v.fuelType || v.fuel_type || '').toLowerCase();
  if (ft.includes('diesel')) return 'diesel';
  if (ft.includes('electric')) return 'electric';
  if (ft.includes('hybrid') && ft.includes('plug')) return 'plug-in_hybrid';
  if (ft.includes('hybrid')) return 'hybrid';
  return 'gasoline';
}

function cleanModel(v) {
  let m = (v.model || '').trim();
  m = m.split(' - ')[0].split('(')[0].trim();
  return m || v.model || '';
}

function extractTrim(v) {
  let m = (v.model || '').trim();
  let base = cleanModel(v);
  if (base === m) return v.trim || '';
  let trim = m.replace(base, '').replace(/^\s*-\s*/, '').trim();
  return trim || v.trim || '';
}

function buildAdditionalImages(v) {
  const images = Array.isArray(v.images) ? v.images : [];
  return images.slice(1, 11).map(img => {
    const src = typeof img === 'string' ? img : img?.url || img?.src || '';
    if (!src) return '';
    return src.startsWith('http') ? src : `${SITE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
  }).filter(Boolean);
}

function buildVehicleOptions(v) {
  const feats = Array.isArray(v.features) ? v.features : [];
  return feats.join(', ');
}

function vehicleToFeedRow(vehicle) {
  return {
    id: buildFeedId(vehicle),
    title: buildTitle(vehicle),
    description: buildDescription(vehicle),
    link: `${SITE_URL}/vehicle/${vehicle.id}`,
    link_template: `${SITE_URL}/vehicle/${vehicle.id}?store={store_code}`,
    image_link: buildImageLink(vehicle),
    additional_images: buildAdditionalImages(vehicle),
    price: vehicle.price ? `${Number(vehicle.price).toFixed(2)} USD` : '',
    google_product_category: 'Vehicles & Parts > Vehicles > Motor Vehicles > Cars, Trucks & Vans',
    condition: mapCondition(vehicle),
    brand: vehicle.make || '',
    model: cleanModel(vehicle),
    trim: extractTrim(vehicle),
    year: vehicle.year || '',
    mileage: vehicle.mileage ? `${parseInt(vehicle.mileage, 10) || 0} Miles` : '0 Miles',
    VIN: vehicle.vin || '',
    color: vehicle.exteriorColor || vehicle.exterior_color || '',
    availability: mapAvailability(vehicle),
    store_code: STORE_CODE,
    body_style: mapBodyStyle(vehicle),
    engine: mapEngine(vehicle),
    vehicle_option: buildVehicleOptions(vehicle),
    product_type: `Vehicles > ${mapBodyStyle(vehicle)} > ${vehicle.make || 'Other'}`
  };
}

function generateTSV(rows) {
  const header = FEED_COLUMNS.join('\t');
  const lines = rows.map(row =>
    FEED_COLUMNS.map(col => {
      const val = String(row[col] || '');
      // Escape tabs and newlines
      return val.replace(/[\t\n\r]/g, ' ');
    }).join('\t')
  );
  return [header, ...lines].join('\n');
}

function generateXML(rows) {
  const items = rows.map(row => {
    const addImgs = (row.additional_images || []).map(u =>
      `      <g:additional_image_link>${escapeXml(u)}</g:additional_image_link>`
    ).join('\n');
    return `    <item>
      <g:id>${escapeXml(row.id)}</g:id>
      <g:title>${escapeXml(row.title)}</g:title>
      <g:description>${escapeXml(row.description)}</g:description>
      <g:link>${escapeXml(row.link)}</g:link>
      <g:link_template>${escapeXml(row.link_template)}</g:link_template>
      <g:image_link>${escapeXml(row.image_link)}</g:image_link>
${addImgs ? addImgs + '\n' : ''}      <g:price>${escapeXml(row.price)}</g:price>
      <g:google_product_category>916</g:google_product_category>
      <g:condition>${escapeXml(row.condition)}</g:condition>
      <g:brand>${escapeXml(row.brand)}</g:brand>
      <g:model>${escapeXml(row.model)}</g:model>${row.trim ? `\n      <g:trim>${escapeXml(row.trim)}</g:trim>` : ''}
      <g:year>${escapeXml(String(row.year))}</g:year>
      <g:mileage>${escapeXml(row.mileage)}</g:mileage>
      <g:VIN>${escapeXml(row.VIN)}</g:VIN>
      <g:color>${escapeXml(row.color)}</g:color>
      <g:availability>${escapeXml(row.availability)}</g:availability>
      <g:store_code>${STORE_CODE}</g:store_code>
      <g:body_style>${escapeXml(row.body_style)}</g:body_style>
      <g:engine>${escapeXml(row.engine)}</g:engine>${row.vehicle_option ? `\n      <g:vehicle_option>${escapeXml(row.vehicle_option)}</g:vehicle_option>` : ''}
      <g:product_type>${escapeXml(row.product_type)}</g:product_type>
    </item>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" xmlns:g="http://base.google.com/ns/1.0">
  <channel>
    <title>Paradise Performance - Vehicle Inventory</title>
    <link>${SITE_URL}</link>
    <description>Quality pre-owned vehicles from Paradise Performance</description>
${items}
  </channel>
</rss>`;
}

function escapeXml(str) {
  return (str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const GoogleVLAFeed = () => {
  const [vehicles, setVehicles] = useState([]);
  const [feedRows, setFeedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [format, setFormat] = useState('tsv'); // 'tsv' or 'xml'
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'raw'
  const [lastGenerated, setLastGenerated] = useState(null);

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await vehicleService.getAvailableVehicles();
      if (fetchError) throw fetchError;

      // Filter to only auto/vehicle types (exclude golf carts, trailers, boats for VLA)
      const autoVehicles = (data || []).filter(v => {
        const vt = (v.vehicleType || v.vehicle_type || '').toLowerCase();
        return !vt.includes('golf') && !vt.includes('trailer') && !vt.includes('boat');
      });

      setVehicles(autoVehicles);
      const rows = autoVehicles.map(vehicleToFeedRow);
      setFeedRows(rows);
      setLastGenerated(new Date());
    } catch (err) {
      console.error('Failed to load vehicles for feed:', err);
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const getFeedContent = useCallback(() => {
    if (format === 'xml') return generateXML(feedRows);
    return generateTSV(feedRows);
  }, [feedRows, format]);

  const handleDownload = () => {
    const content = getFeedContent();
    const ext = format === 'xml' ? 'xml' : 'tsv';
    const mime = format === 'xml' ? 'application/xml' : 'text/tab-separated-values';
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `google-vla-feed.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getFeedContent());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = getFeedContent();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const issueCount = feedRows.filter(r => !r.image_link || !r.price || !r.VIN).length;

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Google Vehicle Listing Ads Feed</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Generate your inventory feed for Google Merchant Center VLA campaigns
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadVehicles}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Vehicles in Feed</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{feedRows.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">With Images</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {feedRows.filter(r => r.image_link).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">With VIN</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {feedRows.filter(r => r.vin).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Issues</p>
          <p className={`text-2xl font-bold mt-1 ${issueCount > 0 ? 'text-amber-500' : 'text-green-600'}`}>
            {issueCount}
          </p>
        </div>
      </div>

      {/* Issues Warning */}
      {issueCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">
              {issueCount} vehicle{issueCount > 1 ? 's' : ''} missing required data
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Vehicles without images, price, or VIN may be rejected by Google Merchant Center.
              Update these listings in the Vehicle Manager to fix.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p className="font-medium">Error loading inventory</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Format Toggle */}
            <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setFormat('tsv')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  format === 'tsv'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                TSV
              </button>
              <button
                onClick={() => setFormat('xml')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  format === 'xml'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                XML
              </button>
            </div>

            {/* View Toggle */}
            <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('preview')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'preview'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FiEye className="w-3.5 h-3.5" />
                Preview
              </button>
              <button
                onClick={() => setViewMode('raw')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'raw'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FiCode className="w-3.5 h-3.5" />
                Raw
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={feedRows.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {copied ? <FiCheck className="w-4 h-4 text-green-500" /> : <FiCopy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={handleDownload}
              disabled={feedRows.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <FiDownload className="w-4 h-4" />
              Download {format.toUpperCase()}
            </button>
          </div>
        </div>

        {lastGenerated && (
          <p className="text-xs text-gray-400 mt-3">
            Generated {lastGenerated.toLocaleString()}
          </p>
        )}
      </div>

      {/* Feed Preview */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex items-center justify-center">
          <FiRefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          <span className="ml-3 text-gray-500">Loading inventory...</span>
        </div>
      ) : viewMode === 'raw' ? (
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-4 overflow-auto max-h-[600px]">
          <pre className="text-xs text-green-400 font-mono whitespace-pre">
            {getFeedContent()}
          </pre>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {FEED_COLUMNS.map(col => (
                    <th
                      key={col}
                      className="px-3 py-2.5 text-left font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap border-b border-gray-200"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {feedRows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {FEED_COLUMNS.map(col => {
                      const val = row[col] || '';
                      const isMissing = (col === 'image_link' || col === 'price' || col === 'vin') && !val;
                      return (
                        <td
                          key={col}
                          className={`px-3 py-2 whitespace-nowrap max-w-[200px] truncate ${
                            isMissing ? 'text-red-400 italic' : 'text-gray-700'
                          }`}
                          title={val || (isMissing ? 'Missing' : '')}
                        >
                          {col === 'link' && val ? (
                            <a
                              href={val}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                              {val.split('/').pop()}
                              <FiExternalLink className="w-3 h-3" />
                            </a>
                          ) : col === 'image_link' && val ? (
                            <a
                              href={val}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {val.length > 40 ? '...' + val.slice(-37) : val}
                            </a>
                          ) : (
                            val || (isMissing ? 'MISSING' : '—')
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {feedRows.length === 0 && (
                  <tr>
                    <td colSpan={FEED_COLUMNS.length} className="px-3 py-8 text-center text-gray-400">
                      No vehicles available for feed
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-3">How to Upload to Google Merchant Center</h3>
        <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
          <li>Download the feed file using the button above (TSV or XML format)</li>
          <li>
            Go to{' '}
            <a
              href="https://merchants.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              Google Merchant Center
              <FiExternalLink className="w-3 h-3" />
            </a>
          </li>
          <li>Navigate to <strong>Products &gt; Feeds</strong></li>
          <li>Click <strong>+ Add product feed</strong> (or update an existing one)</li>
          <li>Select <strong>Vehicle ads</strong> as the feed type</li>
          <li>Choose <strong>Upload a file</strong> and select the downloaded feed</li>
          <li>Review and submit the feed for processing</li>
        </ol>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            <strong>Tip:</strong> Re-download and re-upload the feed whenever your inventory changes.
            For automatic updates, set up a scheduled fetch in Merchant Center pointing to your feed URL.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoogleVLAFeed;
